import React, { useCallback, useEffect, useState } from 'react';
import { Gift, Trophy, Users, Wallet } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  AdminReferral,
  ReferralLeaderboard,
  ReferralStatus,
  referralsApi,
} from '../../api/referrals';
import { PaginatedResponse } from '../../types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import { Table } from '../../components/ui/Table';
import { RewardsPanel } from './RewardsPanel';

const statusColors: Record<ReferralStatus, 'warning' | 'success'> = {
  pending: 'warning',
  completed: 'success',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const UserCell: React.FC<{ user: AdminReferral['referrer'] }> = ({ user }) => (
  <div>
    <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
      {user?.name ?? 'Unknown user'}
    </div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
      {user?.email ?? user?.phoneNumber ?? '—'}
    </div>
  </div>
);

export const Referrals: React.FC = () => {
  const toast = useToast();
  const [search, setSearch] = useState('');

  usePage('Referrals', {
    placeholder: 'Search by code, referrer, or referred user…',
    onSearch: setSearch,
  });

  const [leaderboard, setLeaderboard] = useState<ReferralLeaderboard | null>(null);
  const [data, setData] = useState<PaginatedResponse<AdminReferral> | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | ''>('');
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        page,
        limit: 20,
      };
      const [leaderboardRes, referralsRes] = await Promise.all([
        referralsApi.getLeaderboard(),
        referralsApi.getReferrals(params),
      ]);
      setLeaderboard(leaderboardRes);
      setData(referralsRes);
    } catch (err) {
      toast.error(err, 'Failed to load referrals.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, toast]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchReferrals();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchReferrals]);

  const handleStatusUpdate = async (referral: AdminReferral) => {
    const nextStatus: ReferralStatus =
      referral.status === 'completed' ? 'pending' : 'completed';
    try {
      setUpdatingId(referral.id);
      await referralsApi.updateStatus(referral.id, nextStatus);
      toast.success(
        nextStatus === 'completed'
          ? 'Referral marked completed.'
          : 'Referral moved back to pending.',
      );
      void fetchReferrals();
    } catch (err) {
      toast.error(err, 'Could not update referral.');
    } finally {
      setUpdatingId(null);
    }
  };

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    appearance: 'none',
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M3 4.5L6 7.5L9 4.5\' stroke=\'%23566472\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
          gap: 16,
        }}
      >
        <StatCard
          label="Total referrals"
          value={leaderboard?.stats.totalReferrals ?? 0}
          icon={Users}
          accent="blue"
        />
        <StatCard
          label="Completed"
          value={leaderboard?.stats.completedReferrals ?? 0}
          icon={Trophy}
          accent="green"
        />
        <StatCard
          label="Pending"
          value={leaderboard?.stats.pendingReferrals ?? 0}
          icon={Gift}
          accent="purple"
        />
        <StatCard
          label="Rewards earned"
          value={leaderboard?.stats.totalEarnedLabel ?? '₦0'}
          icon={Wallet}
          accent="neutral"
        />
      </div>

      <RewardsPanel />

      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
              Leaderboard
            </h2>
            <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
              Ranked by completed referrals and total reward value.
            </p>
          </div>
          <Badge variant="info">{leaderboard?.stats.referrers ?? 0} referrers</Badge>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              minWidth: 760,
              borderCollapse: 'collapse',
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--neutral-200)' }}>
                {['Rank', 'Referrer', 'Code', 'Completed', 'Pending', 'Earned', 'Last referral'].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: '12px 14px',
                      textAlign: 'left',
                      fontSize: 12,
                      color: 'var(--neutral-600)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(leaderboard?.leaderboard ?? []).slice(0, 10).map((entry) => (
                <tr key={entry.referrerId}>
                  <td style={{ padding: '12px 14px', fontWeight: 700 }}>#{entry.rank}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontWeight: 600 }}>{entry.referrerName}</div>
                    <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                      {entry.referrerEmail ?? '—'}
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px', fontFamily: 'monospace' }}>{entry.referralCode}</td>
                  <td style={{ padding: '12px 14px' }}>{entry.completedReferrals}</td>
                  <td style={{ padding: '12px 14px' }}>{entry.pendingReferrals}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600 }}>{entry.totalEarnedLabel}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--neutral-600)' }}>
                    {formatDate(entry.lastReferralAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReferralStatus | '')}
            style={filterSelectStyle}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <Table
              data={data.items}
              columns={[
                {
                  key: 'referrer',
                  header: 'Referrer',
                  render: (referral) => <UserCell user={referral.referrer} />,
                },
                {
                  key: 'referredUser',
                  header: 'Referred',
                  render: (referral) =>
                    referral.referredBusiness ? (
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
                          {referral.referredBusiness.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          Business
                        </div>
                      </div>
                    ) : (
                      <UserCell user={referral.referredUser} />
                    ),
                },
                {
                  key: 'code',
                  header: 'Code',
                  render: (referral) => (
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {referral.referralCode}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (referral) => (
                    <Badge variant={statusColors[referral.status]}>{referral.status}</Badge>
                  ),
                },
                {
                  key: 'reward',
                  header: 'Reward',
                  render: (referral) => referral.rewardLabel,
                },
                {
                  key: 'created',
                  header: 'Created',
                  render: (referral) => formatDate(referral.createdAt),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (referral) => (
                    <Button
                      size="sm"
                      variant={referral.status === 'completed' ? 'secondary' : 'primary'}
                      loading={updatingId === referral.id}
                      onClick={() => void handleStatusUpdate(referral)}
                    >
                      {referral.status === 'completed' ? 'Mark pending' : 'Mark complete'}
                    </Button>
                  ),
                },
              ]}
              emptyMessage={
                search || statusFilter
                  ? 'No referrals match your filters'
                  : 'No referrals have been captured yet'
              }
            />
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
};
